import type { NavConfig, NavSubItem } from './types';
import type { Library } from '@/types/catalog';
import { BETA_LABEL, isBetaKind } from '@/utils/credentialTypes';

// Kept for backwards compatibility with callers that want the static
// default (e.g. AppSidebar's fallback prop). New code should use
// `buildLearnerNav()` with the accessible-library records from the Pinia store
// so the Content Library reflects the libraries the learner can actually access.
export const learnerNav: NavConfig = buildLearnerNav([]);

/**
 * Build the learner-side navigation.
 *
 * @param libraries        The libraries the signed-in learner can access
 *   (from GET /api/me/libraries via the accessible-library store), already
 *   ordered for display.
 * @param librariesLoaded  Whether the accessible-library store has resolved.
 *   The Content Library renders no sub-items until it has, so the list appears
 *   once — fully scoped — instead of flashing an empty or partial set.
 */
export function buildLearnerNav(
  libraries: readonly Library[],
  librariesLoaded = false
): NavConfig {
  const browseSubItems: NavSubItem[] = [
    { to: '/recommendations', label: 'Recommendations', featureFlag: 'cleAdvisor' },
    ...libraries.map<NavSubItem>(l => ({
      to: l.kind === 'Microlearning'
        ? `/browse/libraries/${l.id}/practice-areas`
        : `/browse/libraries/${l.id}`,
      label: l.effectiveDisplayName,
      ...(isBetaKind(l.kind) ? { tag: BETA_LABEL } : {}),
    })),
  ];

  return {
    items: [
      {
        to: '/',
        icon: 'dashboard',
        label: 'Dashboard',
      },
      {
        to: '/assignments',
        icon: 'assignment',
        label: 'My Assignments',
      },
      // A pure group header (BPE-680): no `to`, so clicking it only expands or
      // collapses the libraries. Courses are reached through a specific library;
      // there is no cross-library listing.
      {
        icon: 'library_books',
        label: 'Content Library',
        expandable: true,
        subItems: librariesLoaded ? browseSubItems : [],
      },
      {
        to: '/pathways',
        icon: 'route',
        label: 'Learning Pathways',
        expandable: true,
        // Parent `/pathways` already routes to "All Pathways" — no duplicate.
        subItems: [
          { to: '/pathways/custom', label: 'Custom Pathways' },
          { to: '/pathways/barbri', label: 'BARBRI Pathways' },
        ],
      },
      {
        to: '/history',
        icon: 'history',
        label: 'History',
      },
      {
        to: '/bookmarks',
        icon: 'bookmark',
        label: 'Bookmarks',
      },
      {
        to: '/certificates',
        icon: 'workspace_premium',
        label: 'Certificates',
      },
    ],
  };
}
